import { VideoFragment } from 'apollo/fragments/types/VideoFragment';
import {
  createThumbnailCacheKey,
  fetchVideoThumbnailAC,
} from 'features/thumbnail/thumbnail-slice';
import { youtubeRegex } from 'helpers/urlvalidator';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootReducer } from 'rootReducer';
import type { ImageTransformInput } from 'types/graphql';

import { CDN_REGEX } from '../../constants';

export function useVideoThumbnail(
  video: Pick<VideoFragment, 'id' | 'remoteUrl' | 'thumbnail'>,
  width: number,
): string | undefined {
  const { id, remoteUrl, thumbnail } = video;
  const dispatch = useDispatch();

  const youtubeId = remoteUrl ? remoteUrl.match(youtubeRegex)?.[1] : undefined;
  const fallbackThumb = youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/0.jpg`
    : undefined;

  const isCdnThumbnail = thumbnail && CDN_REGEX.test(thumbnail);

  const transform: ImageTransformInput | undefined = useMemo(() => {
    if (!isCdnThumbnail) {
      return undefined;
    }
    return {
      width: Math.floor(width),
      aspectRatio: { width: 16, height: 9 },
    };
  }, [isCdnThumbnail, width]);

  const cacheKey = useMemo(
    () => (isCdnThumbnail ? createThumbnailCacheKey(id, transform) : undefined),
    [id, isCdnThumbnail, transform],
  );

  const signedUrl = useSelector((state: RootReducer) =>
    cacheKey ? state.thumbnail.signedUrls[cacheKey] : undefined,
  );

  useEffect(() => {
    if (!isCdnThumbnail || signedUrl || !transform) {
      return;
    }

    dispatch(
      fetchVideoThumbnailAC.started({
        videoId: id,
        transform,
      }),
    );
  }, [dispatch, id, isCdnThumbnail, signedUrl, transform]);

  if (!thumbnail) {
    return fallbackThumb;
  }

  if (!isCdnThumbnail) {
    return thumbnail;
  }

  return signedUrl;
}
